import { Injectable, Injector, inject } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  concat,
  forkJoin,
  merge,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  retry,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { WaypointService } from './waypoint.service';
import { FailedPlan } from '../../models/failed-plan';
import { LocationService } from '../platform/location.service';
import { MapService } from '../map/map.service';
import { AccountService, SubscriptionType } from '../account/account.service';
import { SettingsService } from '../account/settings.service';
import { HttpService } from './http.service';
import { AnalyticsLoggerService } from '../logging/analytics-logger.service';
import { UtilsService } from '../utils/utils.service';
import { NavigationService } from './navigation.service';
import { NavigationReplayService } from '../map/navigation-replay.service';
import { VehicleService } from '../vehicle/vehicle.service';
import { LatLng } from '../../models/latlng';
import { RangeParams, Waypoint } from '../../models/range-params';
import { ECarSettings } from '../../models/ecar';
import { DisplayMode } from '../../models/display-mode';
import { BetterRouteState } from '../../models/better-route';
import {
  CalculationIntent,
  CalculationState,
  mapRangeHttpError,
  mapRangeResponse,
  mapRouteHttpError,
  mapRouteResponse,
  validateRouteRequest,
} from '../../models/ev-calculation-error-mapper';
import { WidgetService } from '../platform/widget.service';
import { CursorService } from '../map/cursor.service';
import { MapChargingPopupService } from '../map/map-charging-popup.service';

/** Position of ObservableWaypointsParams inside the user-input combineLatest tuple. */
const USER_INPUT_WAYPOINTS_INDEX = 1;

/**
 * Single orchestrator for range and route calculations.
 *
 * Owns the reactive user-input pipeline (combineLatest + debounce +
 * switchMap), the imperative replan/nav-start triggers, and the parallel
 * better-route check stream. Emits a typed CalculationState that
 * MainComponent subscribes to for UI side effects (loading, dialogs,
 * drawing, webview bridge).
 *
 * Constructor-time circular DI is avoided by:
 *   - removing NavigationService from constructor deps (callers own
 *     LastReplanTime and the slider-15s guard);
 *   - resolving NavigationReplayService and NavigationService lazily
 *     via Injector for read-only access at request time.
 */
@Injectable({ providedIn: 'root' })
export class EvCalculationService {

  // ===== Outputs =====

  private readonly state$$ = new BehaviorSubject<CalculationState>({ status: 'idle' });
  public readonly state$: Observable<CalculationState> = this.state$$.asObservable();

  private failedPlan: FailedPlan | null = null;
  public readonly ObservableFailedPlan = new BehaviorSubject<FailedPlan | null>(null);

  public setFailedPlan(param: FailedPlan): void {
    this.failedPlan = param;
    this.ObservableFailedPlan.next(param);
  }

  public getFailedPlan(): FailedPlan | null {
    return this.failedPlan;
  }

  // ===== Internal triggers =====

  private readonly imperativeTrigger$ = new Subject<CalculationIntent>();
  private readonly betterRouteCheckTrigger$ = new Subject<void>();

  /**
   * Start coordinates handed over by requestRangeRecalcFromGps, pending until
   * the next debounced user-input emission decides whether that emission is a
   * pure GPS re-anchor (see resolveUserInputIntent).
   */
  private gpsAnchor: LatLng | null = null;
  /** Previous distinct user-input tuple; lets the next one tell what changed. */
  private lastUserInputTuple: readonly unknown[] | null = null;

  // Kept for backwards compat — Step 3 wired MainComponent to this. Step 4
  // owns the better-route HTTP call internally, so MainComponent no longer
  // subscribes here.
  public readonly onBetterRouteCheck$: Observable<void> = this.betterRouteCheckTrigger$.asObservable();

  // Subscriptions live for the lifetime of the singleton — no cleanup
  // needed (the service is providedIn: root).

  private readonly waypointService = inject(WaypointService);
  private readonly locationService = inject(LocationService);
  private readonly mapService = inject(MapService);
  private readonly accountService = inject(AccountService);
  private readonly settingsService = inject(SettingsService);
  private readonly httpService = inject(HttpService);
  private readonly utilsService = inject(UtilsService);
  private readonly injector = inject(Injector);
  private readonly vehicleService = inject(VehicleService);
  private readonly cursorService = inject(CursorService);
  private readonly mapChargingPopupService = inject(MapChargingPopupService);
  private readonly widgetService = inject(WidgetService);

  constructor() {
    this.startPipeline();
  }

  // ===== Lazy lookups =====

  // The following services all transitively inject NavigationService, which
  // injects this facade — a construction-time cycle. Resolve lazily so the
  // graph is acyclic at constructor time; the first usage happens after
  // every service is fully constructed.
  private get navigationReplayService(): NavigationReplayService {
    return this.injector.get(NavigationReplayService);
  }
  private get navigationService(): NavigationService {
    return this.injector.get(NavigationService);
  }
  private get loggerService(): AnalyticsLoggerService {
    return this.injector.get(AnalyticsLoggerService);
  }

  // ===== Public API =====

  /**
   * Re-anchor the route start to current GPS (or keep current coords in
   * replay mode) and trigger an immediate, undebounced calculation.
   *
   * `forceRecalc=false` (e.g. when a slider was just dragged) skips the
   * imperative trigger; the user-input pipeline will pick up the slider
   * change naturally within the next debounce window.
   *
   * The caller owns NavigationService.LastReplanTime — write it before
   * calling, to avoid a circular DI through NavigationService.
   */
  requestReplan(opts: { forceRecalc?: boolean } = {}): void {
    const forceRecalc = opts.forceRecalc ?? true;
    this.captureStartCoords();
    if (forceRecalc) {
      this.imperativeTrigger$.next(CalculationIntent.REPLAN);
    }
  }

  /**
   * Plan an initial route from the current GPS position when navigation
   * starts. Same shape as requestReplan but tagged NAVIGATION_START so
   * subscribers / telemetry can distinguish.
   */
  requestNavigationStart(): void {
    this.captureStartCoords();
    this.imperativeTrigger$.next(CalculationIntent.NAVIGATION_START);
  }

  /**
   * Trigger a "is there a faster alternative?" route calculation. Runs on
   * a parallel stream that never cancels — and is never cancelled by —
   * the main user-input pipeline.
   */
  requestBetterRouteCheck(): void {
    this.betterRouteCheckTrigger$.next();
  }

  /**
   * Range mode follows the car: re-anchor the start waypoint to the current
   * GPS fix. This rides the normal debounced user-input pipeline, but the
   * resulting calculation is tagged GPS_DRIFT as long as nothing else changed
   * inside the same debounce window, so the UI can refresh the range in place
   * instead of re-framing it.
   */
  requestRangeRecalcFromGps(start: LatLng): void {
    this.gpsAnchor = start;
    this.waypointService.setStartCoordsParams(start);
  }

  // ===== Pipeline =====

  private startPipeline(): void {
    // Main pipeline. Two parallel sources merged into one switchMap so that
    // a newer trigger (user input OR imperative replan) cancels an in-flight
    // calculation. The user-input branch is debounced; imperative replan is
    // not.
    //
    // Merge real ECarSettings changes with force-recalc triggers so that
    // waypointService.paramsUpdate$ (routing options, battery, arrive)
    // can still trigger a recalculation without owning ECarSettings state.
    const ecarSettings$ = merge(
      this.vehicleService.ObservableECarSettings.pipe(
        filter((x): x is ECarSettings => x != null),
      ),
      this.waypointService.paramsUpdate$.pipe(
        withLatestFrom(this.vehicleService.ObservableECarSettings),
        map(([, s]) => s),
        filter((s): s is ECarSettings => s != null),
      ),
    );

    const userInput$ = combineLatest([
      ecarSettings$,
      this.waypointService.ObservableWaypointsParams.pipe(filter((x): x is NonNullable<typeof x> => x != null && x.length > 0)),
      this.vehicleService.ObservableSelectedCar.pipe(filter((x: any) => x != null)),
      this.vehicleService.ObservableChargingStations.pipe(filter((x: any) => x != null)),
      this.settingsService.ObservableRoutingSettings.pipe(filter((x: any) => x != null)),
      this.waypointService.ObservableSelectedDisplayMode,
    ]).pipe(
      debounceTime(400),
      distinctUntilChanged((a, b) => this.shallowEqualTuple(a, b)),
      map(tuple => ({
        // Snapshot the assembled RangeParams from the source of truth, so
        // any field not in the combineLatest (e.g. TempWindParams) is
        // included consistently.
        rangeParams: this.getRangeParams(this.vehicleService.getECarSettings()!, this.waypointService.getWaypointsParams()),
        intent: this.resolveUserInputIntent(tuple),
      })),
    );

    const imperative$ = this.imperativeTrigger$.pipe(
      map(intent => ({
        rangeParams: this.getRangeParams(this.vehicleService.getECarSettings()!, this.waypointService.getWaypointsParams()),
        intent,
      })),
    );

    const chargingStatusRoute$ = this.mapChargingPopupService.statusChanged$.pipe(
      filter(() => this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE),
      map(() => ({
        rangeParams: this.getRangeParams(this.vehicleService.getECarSettings()!, this.waypointService.getWaypointsParams()),
        intent: CalculationIntent.USER_INPUT,
      })),
    );

    const chargingFiltersRoute$ = this.mapService.chargingFiltersChanged$.pipe(
      filter(() => {
        const mode = this.waypointService.getSelectedDisplayMode();
        return mode === DisplayMode.ROUTE || mode === DisplayMode.NAVIGATION;
      }),
      map(() => ({
        rangeParams: this.getRangeParams(this.vehicleService.getECarSettings()!, this.waypointService.getWaypointsParams()),
        intent: CalculationIntent.USER_INPUT,
      })),
    );

    merge(userInput$, imperative$, chargingStatusRoute$, chargingFiltersRoute$).pipe(
      switchMap(({ rangeParams, intent }) => this.dispatchByMode(rangeParams, intent)),
    ).subscribe(state => this.state$$.next(state));

    // Better-route check on its own stream so user input never cancels it
    // and it never cancels user input.
    this.betterRouteCheckTrigger$.pipe(
      switchMap(() => this.dispatchBetterRouteCheck()),
    ).subscribe();
  }

  private dispatchByMode(rangeParams: RangeParams, intent: CalculationIntent): Observable<CalculationState> {
    if (rangeParams.Waypoints.length >= 2) {
      return this.dispatchRoute(rangeParams, intent, /* checkBetterRoute */ false);
    }
    if (this.waypointService.getSelectedDisplayMode() === DisplayMode.RANGE) {
      return this.dispatchRange(rangeParams, intent);
    }
    return EMPTY;
  }

  private dispatchRoute(
    rangeParams: RangeParams,
    intent: CalculationIntent,
    checkBetterRoute: boolean,
  ): Observable<CalculationState> {
    // Pre-flight: distance limits (hard cap and free-tier cap)
    // The widget is licensed through its api_key, so the 500 km free-tier cap does not apply
    // to it. This exempts the cap only: the subscription type itself stays Free, so every
    // other paid feature (live traffic, ...) remains off in widget mode.
    const isFreeTier = this.accountService.getUserSubscriptionType() === SubscriptionType.Free
      && !this.widgetService.isWidget();
    const validationErr = validateRouteRequest(
      rangeParams,
      isFreeTier,
      (a, b) => this.utilsService.distanceBetweenCoordinates(a, b),
    );
    if (validationErr) {
      return of({
        status: 'error' as const,
        intent,
        kind: 'route' as const,
        error: validationErr,
        rangeParams,
      });
    }

    // Selected-car guard: empty ChargerTypes means user hasn't picked any
    // compatible connector; surface as a structured error so MainComponent
    // opens the SelectChargingStation dialog.
    const selectedCar = this.vehicleService.getSelectedCar();
    if (!selectedCar || selectedCar.ChargerTypes?.length === 0) {
      return of({
        status: 'error' as const,
        intent,
        kind: 'route' as const,
        error: { kind: 'no_charger_types' as const },
        rangeParams,
      });
    }
    if (!selectedCar.Name) {
      return EMPTY;
    }

    // Build HTTP params (preserves filters, profile, routing settings)
    const filters = this.collectChargingFilters();
    const bearing = this.cursorService.getBearing();
    const userProfile = this.accountService.getIsAuthorized()
      ? this.accountService.getUserProfile()?.Profile?.Id!.toString() ?? ''
      : '';
    const routingSettings = this.settingsService.getRouting();

    const routingCall = this.httpService.getRouting(
      rangeParams,
      selectedCar,
      filters.disabledChargingStation,
      filters.preferedChargingStation,
      // HttpService.getRouting is typed number[] but the original
      // MainComponent.getRoute passed stringified IDs — preserve that.
      filters.disabledOperator as unknown as number[],
      filters.preferedOperator as unknown as number[],
      this.waypointService.getSelectedDisplayMode(),
      bearing,
      routingSettings,
      null as any,
      userProfile,
    );

    const reverseGeocodes: Observable<any>[] = rangeParams.Waypoints.map(wp =>
      wp.lat != null && wp.lng != null
        ? this.httpService.getReverseGeocode(wp.lat, wp.lng)
        : (of(null) as Observable<any>),
    );

    let httpCall$: Observable<any> = forkJoin([routingCall, ...reverseGeocodes]).pipe(
      map(resp => {
        const [route, ...geocodes] = resp as any[];
        // Reverse geocode only supplies address fields; keep the server's
        // per-waypoint data (weathercode, temp, ...) and overlay the address.
        const serverInfo: any[] = Array.isArray(route?.waypointsInfo) ? route.waypointsInfo : [];
        route.waypointsInfo = geocodes.map((geocode, i) => ({ ...(serverInfo[i] ?? {}), ...(geocode ?? {}) }));
        return route;
      }),
    );

    // Navigation-mode retry: if a route HTTP fails while navigating, retry
    // after 5 s. Better-route checks never retry. Non-navigation calls
    // surface the error to the user immediately.
    if (!checkBetterRoute) {
      httpCall$ = httpCall$.pipe(
        retry({
          delay: (err) => {
            if (this.navigationService.navigation) {
              return timer(5000);
            }
            return throwError(() => err);
          },
        }),
      );
    }

    const pendingState: CalculationState = {
      status: 'pending',
      intent,
      kind: 'route',
      rangeParams,
    };

    return concat(
      // Better-route checks are background — no UI pending state.
      checkBetterRoute ? EMPTY : of(pendingState),
      httpCall$.pipe(
        map(resp => {
          const respErr = mapRouteResponse(resp);
          if (respErr) {
            return {
              status: 'error' as const,
              intent,
              kind: 'route' as const,
              error: respErr,
              rangeParams,
            };
          }
          return {
            status: 'success' as const,
            intent,
            kind: 'route' as const,
            data: resp,
            rangeParams,
          };
        }),
        catchError(err => of<CalculationState>({
          status: 'error',
          intent,
          kind: 'route',
          error: mapRouteHttpError(err),
          rangeParams,
        })),
      ),
    );
  }

  private dispatchRange(
    rangeParams: RangeParams,
    intent: CalculationIntent,
  ): Observable<CalculationState> {
    const selectedCar = this.vehicleService.getSelectedCar();
    if (!selectedCar || selectedCar.ChargerTypes?.length === 0) {
      return of({
        status: 'error' as const,
        intent,
        kind: 'range' as const,
        error: { kind: 'no_charger_types' as const },
        rangeParams,
      });
    }
    if (!selectedCar.Name) {
      return EMPTY;
    }

    const userProfile = this.accountService.getIsAuthorized()
      ? this.accountService.getUserProfile()?.Profile?.Id!.toString() ?? ''
      : '';

    const pendingState: CalculationState = {
      status: 'pending',
      intent,
      kind: 'range',
      rangeParams,
    };

    const httpCall$ = this.httpService.getRange(
      rangeParams,
      selectedCar,
      this.waypointService.getSelectedDisplayMode(),
      userProfile,
      this.loggerService.getRangeLogs(),
    );

    return concat(
      of(pendingState),
      httpCall$.pipe(
        tap(() => this.loggerService.onLogsUpdated(true)),
        map(resp => {
          const respErr = mapRangeResponse(resp);
          if (respErr) {
            return {
              status: 'error' as const,
              intent,
              kind: 'range' as const,
              error: respErr,
              rangeParams,
            };
          }
          return {
            status: 'success' as const,
            intent,
            kind: 'range' as const,
            data: resp,
            rangeParams,
          };
        }),
        catchError(err => {
          this.loggerService.onLogsUpdated(false);
          return of<CalculationState>({
            status: 'error',
            intent,
            kind: 'range',
            error: mapRangeHttpError(err),
            rangeParams,
          });
        }),
      ),
    );
  }

  public getRangeParams(ecarSettings: ECarSettings, waypoints: Waypoint[]): RangeParams {
    const rp = new RangeParams();
    rp.RangeInputParams = ecarSettings;
    rp.Waypoints = waypoints;
    return rp;
  }

  private dispatchBetterRouteCheck(): Observable<void> {
    const rangeParams = this.getRangeParams(this.vehicleService.getECarSettings()!, this.waypointService.getWaypointsParams());
    return this.dispatchRoute(rangeParams, CalculationIntent.BETTER_ROUTE_CHECK, /* checkBetterRoute */ true).pipe(
      tap(state => {
        if (state.status === 'success' && state.kind === 'route') {
          // "Is it actually ≥ 10 min faster?" check, formerly in
          // MainComponent.validateBetterRoute. Emits to ObservableBetterRoute
          // for MainComponent to surface the popup.
          this.navigationService.ObservableNextTurn.pipe(first()).subscribe(nextTurn => {
            if (!nextTurn) return;
            // state.data is the raw HTTP route response; the parsed route
            // duration lives under .routeData.time (see data-parser.service).
            const minutesDifference = nextTurn.TimeToDest - state.data.routeData.time;
            if (minutesDifference >= 10) {
              this.navigationService.ObservableBetterRoute.next({
                state: BetterRouteState.ShowPopup,
                minutesDifference,
                routeData: state.data,
                rangeParams,
              });
            }
          });
        }
      }),
      map(() => void 0),
    );
  }

  // ===== Helpers =====

  private captureStartCoords(): void {
    // Real navigation always re-anchors the route start at the current GPS
    // position. Replay mode must do the same as soon as the simulation is
    // actually feeding fake positions, otherwise a mid-route replan would be
    // planned from the original origin and the replay would restart from the
    // beginning of the old route instead of continuing on the new one.
    // Before the replay starts (navigation start) there is no fake position
    // yet, so the planned start is kept as-is.
    const useCurrentPosition =
      !this.navigationReplayService.replayMode || this.locationService.getFakeGPSReplayMode();

    if (useCurrentPosition) {
      const lastPosition = this.locationService.getLastPosition();
      if (lastPosition) {
        this.waypointService.setStartCoordsParams(
          new LatLng(lastPosition.Latitude, lastPosition.Longitude),
        );
        return;
      }
    }

    if (this.navigationReplayService.replayMode) {
      // Replay mode without a usable position: keep current start, just touch
      // the setter so any downstream listeners refresh.
      this.waypointService.setStartCoordsParams(this.waypointService.getStartCoordsParams());
    }
  }

  private collectChargingFilters() {
    let disabledChargingStation: string[] = [];
    let preferedChargingStation: string[] = [];
    let disabledOperator: string[] = [];
    let preferedOperator: string[] = [];

    if (this.settingsService.acceptCookies) {
      const stored = localStorage.getItem('skippedChargingStation');
      if (stored && stored !== 'null' && stored !== 'undefined') {
        try { disabledChargingStation = JSON.parse(stored) ?? []; } catch { disabledChargingStation = []; }
      }
      disabledChargingStation = disabledChargingStation.concat(this.settingsService.getDisabledChargingStationIds());
      preferedChargingStation = this.settingsService.getFavoriteChargingStationIds();
      disabledOperator = this.settingsService.getDisabledChargingOperatorIds().map(el => el.toString());
      preferedOperator = this.settingsService.getFavoriteChargingOperatorIds().map(el => el.toString());
    }

    return { disabledChargingStation, preferedChargingStation, disabledOperator, preferedOperator };
  }

  /**
   * A debounced user-input emission is a GPS_DRIFT calculation only when the
   * GPS re-anchor is the sole change since the previous emission: the single
   * waypoint is the anchored fix and every other input still holds the
   * identical value. A battery, car or settings change landing in the same
   * debounce window makes it a regular USER_INPUT calculation, which re-frames
   * the range. (The re-anchor's own paramsUpdate$ tick re-emits the latest
   * ECarSettings object unchanged, so it does not count as a change here.)
   */
  private resolveUserInputIntent(tuple: readonly unknown[]): CalculationIntent {
    const prev = this.lastUserInputTuple;
    this.lastUserInputTuple = tuple;
    const anchor = this.gpsAnchor;
    this.gpsAnchor = null;
    if (!anchor || !prev) return CalculationIntent.USER_INPUT;

    const onlyWaypointsChanged = tuple.every((value, i) => i === USER_INPUT_WAYPOINTS_INDEX || value === prev[i]);
    const waypoints = tuple[USER_INPUT_WAYPOINTS_INDEX] as Waypoint[];
    const anchored = waypoints.length === 1 && waypoints[0].lat === anchor.lat && waypoints[0].lng === anchor.lng;
    return onlyWaypointsChanged && anchored ? CalculationIntent.GPS_DRIFT : CalculationIntent.USER_INPUT;
  }

  private shallowEqualTuple(a: unknown[], b: unknown[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }
}
